import { useState } from 'react';
import documentApi from '../../../../../../services/api/documentApi'
import { AcceptedFileExts, AcceptedMimeTypes, fileTypeWillOpen } from '../../Shared/resources/DoxFileTypes';
import { DoxFileOperationStatus } from '../services/doxExplorerConsts';
import { useDoxExplorerParent } from '../../../../../../hooks/useDoxExplorerParentHook';

export const useFileDownload = () => {
    const [loadingFiles, setLoadingFiles] = useState([])

    const [translateString, handleUnauthorized] = useDoxExplorerParent()
    const api = documentApi.create(handleUnauthorized);

    const handleOpenFileByType = async (action, ticketId, documentType) => {
        try {
            var response = await api.downloadFileByType(ticketId, documentType);
            if (response?.status === 200 && response.data) {
                 // get the file name from response header
                const contentDisposition = response.headers['content-disposition'];
                let cleanFileName = 'unknown';
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (fileNameMatch && fileNameMatch[1]) {
                        cleanFileName = fileNameMatch[1].replace(/['"]/g, '');
                    }
                }
                cleanFileName = cleanFileName.replace(/[/\\?%*:|"<>]/g, '').replace("R_partition", "Répartition");
                let fileNameParts = cleanFileName.split('.')
                // find file type
                let fileType = ''
                if (fileNameParts.length > 1) {
                    fileType = '.' + fileNameParts[fileNameParts.length - 1]
                }

                // parse binary response data into fancy blob
                let fileLink = URL.createObjectURL(response.data);

                // do something with it
                downloadFile(fileLink, cleanFileName, action === 'view' && fileTypeWillOpen(fileType))

                return DoxFileOperationStatus.COMPLETE
            } else if (response && response.status === 404) {
                return DoxFileOperationStatus.FAIL_NO_SUCH_FILE
            } else {
                return DoxFileOperationStatus.FAIL_TRANSIENT
            }
        } catch (error) {
            return DoxFileOperationStatus.FAIL_TRANSIENT
        }
    }

    const handleFileAction = async (action, documentId, fileNamePart, fileType, callback = () => { }) => {
        if (loadingFiles.includes(documentId)) {
            callback(documentId, DoxFileOperationStatus.IN_PROGRESS)
            return
        }

        let newTab = false
        setLoadingFiles([...loadingFiles, documentId])

        try {
            if (action === 'recover') {
                var response = await api.recoverFile(documentId);
                if (response) {
                    callback(documentId, DoxFileOperationStatus.COMPLETE)
                }
                else {
                    callback(documentId, DoxFileOperationStatus.FAIL_TRY_AGAIN)
                }
            }
            else {
                var response = await api.downloadFile(documentId);
                if (response && response.status === 200 && response.data) {
                    let fileName = fileNamePart + fileType;
                    let fileLink = URL.createObjectURL(response.data);

                    if (action === 'print') {
                        if (navigator.userAgent.toLowerCase().indexOf('firefox') === -1) {
                            printFile(fileLink, newTab);
                        } else {
                            downloadFile(fileLink, fileName, true)
                        }
                    } else {
                        downloadFile(fileLink, fileName, action === 'view' && fileTypeWillOpen(fileType))
                    }

                    callback(documentId, DoxFileOperationStatus.COMPLETE)
                } else if (response && response.status === 404) {
                    callback(documentId, DoxFileOperationStatus.FAIL_NO_SUCH_FILE)
                } else {
                    callback(documentId, DoxFileOperationStatus.FAIL_TRANSIENT)
                }
            }
        } catch (error) {
            console.log(error)
            callback(documentId, DoxFileOperationStatus.FAIL_TRANSIENT)
        } finally {
            setLoadingFiles(loadingFiles.filter(x => x.documentId !== documentId))
        }
    }

    const base64ToArrayBuffer = (base64) => {
        var binary_string = window.atob(base64);
        var len = binary_string.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }

    const getMimeType = (fileType) => {
        let idx = AcceptedFileExts.indexOf((fileType || '').toLowerCase())
        if (idx !== -1) {
            return AcceptedMimeTypes[idx]
        }
        return 'application/pdf';
    }

    const makeBlobLink = (data, fileType) => {
        // hacky hack: make object uri from blob (yuck, but it works)
        var mimeType = getMimeType(fileType);

        let blob = new Blob([data], { type: mimeType })
        return URL.createObjectURL(blob)
    }

    const downloadFile = (fileLink, fileName, openInTab) => {
        if (!openInTab) {
            // Trigger direct download
            const link = document.createElement('a');
            link.href = fileLink;
            link.download = fileName;
            // Append to DOM for compatibility
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link); // Clean up the DOM
        } else {
            // Open a blank new tab and write a wrapper page so the file displays
            // inline while the tab title and download link carry the correct filename.
            const downloadText = translateString('Documents.Download');
            const newTab = window.open('', '_blank');
            if (newTab) {
                const safeFileName = fileName
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;');
                newTab.document.write(
                    '<!DOCTYPE html><html><head><meta charset="utf-8">'
                    + '<title>' + safeFileName + '</title>'
                    + '<style>'
                    + '* { margin: 0; padding: 0; box-sizing: border-box; }'
                    + 'body { display: flex; flex-direction: column; height: 100vh; font-family: sans-serif; }'
                    + '.toolbar { display: flex; align-items: center; padding: 6px 12px; background: #f5f5f5; border-bottom: 1px solid #ddd; gap: 12px; }'
                    + '.toolbar span { font-size: 13px; color: #333; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }'
                    + '.toolbar a { font-size: 13px; text-decoration: none; color: #0078d4; white-space: nowrap; }'
                    + '.toolbar a:hover { text-decoration: underline; }'
                    + 'iframe { flex: 1; border: none; width: 100%; }'
                    + '</style></head><body>'
                    + '<div class="toolbar">'
                    + '<span>' + safeFileName + '</span>'
                    + `<a href="${fileLink}" download="${safeFileName}">${downloadText}</a>`
                    + '</div>'
                    + '<iframe src="' + fileLink + '#toolbar=0"></iframe>'
                    + '</body></html>'
                );
                newTab.document.close();
            }
        }
        // Clean up Blob URLs if applicable
        if (fileLink.startsWith("blob:")) {
            window.addEventListener('focus', () => URL.revokeObjectURL(fileLink), { once: true });
        }
    };

    const bulkDownload = async (ticketId, zipFileName, documentIds) => {
        var response = await api.downloadZip(ticketId, zipFileName, documentIds);
        if (response?.status === 200) {
            const blob = new Blob([response.data], { type: 'application/zip' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', zipFileName);
            link.click();
        }
        else {
            throw new Error('Bulk Download endpoint failure');
        }
    }

    const printFile = (pdfLink, newTab) => {
        if (newTab) {
            let myWindow = window.open();
            myWindow.location.href = pdfLink
        }
        else {
            let iframe = window.frames['file-print']

            // javascript IFRAME magic! (ew.)
            if (!iframe) {
                iframe = document.createElement('iframe')
                iframe.id = 'file-print'
                iframe.style.visibility = 'hidden'
                iframe.onload = function () {
                    setTimeout(function () {
                        iframe.focus();
                        iframe.contentWindow.print();
                    }, 1);
                };
                document.body.appendChild(iframe)
            }

            iframe.src = pdfLink;
        }
    }

    return [loadingFiles, handleFileAction, handleOpenFileByType, bulkDownload]
}